import * as React from 'react';
import cx from 'classnames';
import {
  toUpper, isFunction, isEmpty, map,
} from 'lodash';

import { useLocation } from 'react-router-dom';
import { useEventContext } from '@frontend/app/context/EventContext';
import { EventName } from '@common';

import {
  Editor as DraftEditor,
  EditorState,
  ContentState,
  Modifier,
  RichUtils,
  KeyBindingUtil,
  convertToRaw,
  DraftHandleValue,
  RawDraftContentState,
  getDefaultKeyBinding,
  DraftEditorCommand,
  EditorChangeType,
  SelectionState,
  DraftDecorator,
  AtomicBlockUtils,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { logger } from '@common';
import { ResourceType } from '@frontend/app/types/globalTypes';
import { IMember, IMessageTemplate } from '@frontend/app/hooks';
import { TrashIcon } from '@revfluence/fresh-icons/regular/esm';
import { IAttachment } from '@frontend/app/components';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { Button } from '@/shadcn/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shadcn/components/ui/tooltip';
import { StyleControls } from './StyleControls';
import { getDecorator } from './decorator';
import {
  editorStateFromValue, toggleLinkState, getSelectedEntityType, exportHTML,
} from './utils';
import { imageResizeStore } from './utils/imageResizeStore';
import { useImageUpload } from './hooks/useImageUpload';
import { useVideoUpload } from './hooks/useVideoUpload';
import { TTextColor, THighlightColor, TTextAlign, TIndentType, ImageEntityData, VideoEntityData, CTAButtonEntityData, TableEntityData, DividerEntityData } from './StyleControls/types';
import { FONT_FAMILY_MAP } from './constants';
import { AtomicBlock } from './components/AtomicBlock';
import { ImageLoadingPlaceholder } from './components/ImageLoadingPlaceholder';
import { CTAButtonConfigModal } from '../MessageComposer/EmailEditor/CTAButton/CTAButtonConfigModal';
import { CTAButtonConfig } from '../MessageComposer/EmailEditor/CTAButton';

export interface IEditorState {
  raw: RawDraftContentState;
  html: string;
}
interface IProps {
  plaintextOnly?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  // initial value
  initialValue?: string | RawDraftContentState;
  currentValue?: string | RawDraftContentState;
  signaturePreview?: string;
  convertFromHTML?: boolean;
  onChange?(result: IEditorState): void;
  attachments?: IAttachment[];
  resourceType?: ResourceType;
  onDeleteAttachment?(id: string): void;
  onFilesSelected?(files: FileList): void;
  showProgramVariables?: boolean;
  enableVariableReplacement?: boolean;
  enableEmailTemplate?: boolean;
  replaceTemplateVariables?: boolean;
  members?: IMember[];
  invalidMemberIdsForField?: {
    [field: string]: number[];
  };
  additionalVariables?: { [key: string]: { label: string } };
  canSend?: boolean;
  errorMessage?: string;
  actions?: JSX.Element[];
  enableMessageTemplate?: boolean;

  errorHeader?: React.ReactNode;

  decorators?: DraftDecorator[];
  className?: string;
  hasFixedHeight?: boolean;
  actionsDisabled?: boolean;
  onClose?: () => void;
  onTemplateSelected?: (template: IMessageTemplate) => void;
  onTableModalOpen?: () => void;
  onTableEdit?: (tableData: TableEntityData, entityKey: string) => void;
  onDeleteTable?: (entityKey: string) => void;
}
interface IInsertEntityParams {
  type: string;
  label?: string;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  data?: any;
}
export interface IEditor {
  focus(): void;
  reset(): void;
  getValue(): IEditorState;
  setValue(value: string | RawDraftContentState): void;
  insertText(text: string): void;
  insertEntity(params: IInsertEntityParams): void;
  insertTable(tableData: TableEntityData): void;
  updateTable(entityKey: string, tableData: TableEntityData): void;
  deleteTable(entityKey: string): void;
}
interface KeyboardShortcuts {
  ZERO: 48;
  ONE: 49;
  TWO: 50;
  THREE: 51;
  FOUR: 52;
  FIVE: 53;
  SIX: 54;
  SEVEN: 55;
  EIGHT: 56;
  B: 66;
  E: 69;
  I: 73;
  K: 75;
  T: 84;
  V: 86;
  X: 88;
}

const KeyCode: KeyboardShortcuts = {
  ZERO: 48,
  ONE: 49,
  TWO: 50,
  THREE: 51,
  FOUR: 52,
  FIVE: 53,
  SIX: 54,
  SEVEN: 55,
  EIGHT: 56,
  B: 66,
  E: 69,
  I: 73,
  K: 75,
  T: 84,
  V: 86,
  X: 88,
};

type TCustomKeybindingType =
  | 'unstyled'
  | 'header-one'
  | 'header-two'
  | 'header-three'
  | 'header-four'
  | 'header-five'
  | 'header-six'
  | 'ordered-list-item'
  | 'unordered-list-item'
  | 'link'
  | 'strikethrough'
  | 'code'
  | 'inline-image'
  | 'inline-video'
  | 'cta-button'
  | 'table'
  | DraftEditorCommand;

const KEYCODE_TO_BLOCK_TYPE: {
  [keyCode: number]: TCustomKeybindingType;
} = {
  [KeyCode.ZERO]: 'unstyled',
  [KeyCode.ONE]: 'header-one',
  [KeyCode.TWO]: 'header-two',
  [KeyCode.THREE]: 'header-three',
  [KeyCode.FOUR]: 'header-four',
  [KeyCode.FIVE]: 'header-five',
  [KeyCode.SIX]: 'header-six',
  [KeyCode.SEVEN]: 'ordered-list-item',
  [KeyCode.EIGHT]: 'unordered-list-item',
  [KeyCode.B]: 'cta-button', // Cmd+B
};

const { useState, useEffect, useImperativeHandle } = React;
const { hasCommandModifier } = KeyBindingUtil;

import styles from './Editor.module.scss';
import { AttachmentItem } from '../MessageComposer';

/**
 * @type {React.FC}
 */
const EditorComponent: React.RefForwardingComponent<IEditor, IProps> = (props, ref) => {
  const addEvent = useEventContext();
  const location = useLocation();

  const [blockStyle, setBlockStyle] = useState<string>();
  const [currentTextColor, setCurrentTextColor] = useState<TTextColor>('#000000');
  const [currentHighlightColor, setCurrentHighlightColor] = useState<THighlightColor>('transparent');
  const [currentAlignment, setCurrentAlignment] = useState<TTextAlign>('left');
  const [uploadingImages, setUploadingImages] = useState<{ [key: string]: { fileName: string; uploadId: string } }>({});
  const [uploadingVideos, setUploadingVideos] = useState<{ [key: string]: { fileName: string; uploadId: string } }>({});
  const [isCTAModalOpen, setIsCTAModalOpen] = useState(false);
  const { emailV2 } = useClientFeatures();

  const { uploadImage, validateImageFile } = useImageUpload();
  const { uploadVideo, validateVideoFile } = useVideoUpload();

  // Destructure props to avoid dependency issues
  const { onChange } = props;

  // Custom style map for Draft.js
  const customStyleMap = React.useMemo(() => {
    const styleMap: { [key: string]: React.CSSProperties } = {};

    // Font families with proper fallbacks
    Object.entries(FONT_FAMILY_MAP).forEach(([font, fallback]) => {
      styleMap[`FONT_FAMILY_${font.toUpperCase().replace(/\s/g, '_')}`] = {
        fontFamily: fallback,
      };
    });

    // Font sizes
    const sizes = ['12', '14', '16', '18', '20', '24', '28', '32'];
    sizes.forEach((size) => {
      styleMap[`FONT_SIZE_${size}`] = {
        fontSize: `${size}px`,
      };
    });

    // Text colors - generate common colors
    const colors = [
      '000000', '333333', '666666', '999999', 'CCCCCC', 'FFFFFF',
      'FF0000', 'FF8000', 'FFFF00', '80FF00', '00FF00', '00FF80',
      '00FFFF', '0080FF', '0000FF', '8000FF', 'FF00FF', 'FF0080',
    ];
    colors.forEach((color) => {
      styleMap[`TEXT_COLOR_${color}`] = {
        color: `#${color}`,
      };
    });

    // Highlight colors
    colors.forEach((color) => {
      styleMap[`HIGHLIGHT_COLOR_${color}`] = {
        backgroundColor: `#${color}`,
      };
    });

    styleMap.HIGHLIGHT_NONE = {
      backgroundColor: 'transparent',
    };

    // Text alignment styles
    styleMap.ALIGN_CENTER = {
      textAlign: 'center',
      display: 'block',
    };
    styleMap.ALIGN_RIGHT = {
      textAlign: 'right',
      display: 'block',
    };
    styleMap.ALIGN_JUSTIFY = {
      textAlign: 'justify',
      display: 'block',
    };

    return styleMap;
  }, []);

  const [editorDisabled, setEditorDisabled] = useState(false);

  // Use a ref to store updateEntityData to avoid circular dependencies
  const updateEntityDataRef = React.useRef<((newContentState: ContentState, changeType?: EditorChangeType) => void) | null>(null);

  // Memoize decorator to prevent unnecessary re-creation
  const decorator = React.useMemo(() => getDecorator({
    decorators: props.decorators,
    props: {
      updateEntityData: (newContentState: ContentState, changeType?: string) => {
        if (updateEntityDataRef.current) {
          updateEntityDataRef.current(newContentState, changeType as EditorChangeType);
        }
      },
      setEditorDisabled,
    },
  }), [props.decorators]);

  // Initialize editorState early to avoid temporal dead zone
  const [editorState, setEditorState] = useState<EditorState>(() =>
    editorStateFromValue({
      value: props.currentValue || props.initialValue,
      decorator,
      isHtml: props.convertFromHTML,
    })
  );

  const onEditorStateChange = React.useCallback((newState: EditorState) => {
    const contentChanged = newState.getCurrentContent() !== editorState.getCurrentContent();

    if (contentChanged && isFunction(onChange)) {
      const htmlOutput = exportHTML(newState);

      onChange({
        raw: convertToRaw(newState.getCurrentContent()),
        html: htmlOutput,
      });
    } else if (isFunction(onChange)) {
      const htmlOutput = exportHTML(newState);
      onChange({
        raw: convertToRaw(newState.getCurrentContent()),
        html: htmlOutput,
      });
    }

    setEditorState(newState);
  }, [editorState, onChange]);

  const updateEntityData = React.useCallback((newContentState: ContentState, changeType: EditorChangeType = 'apply-entity') => {
    // For entity updates, we need to be careful not to modify block text
    // Create a new editor state with the updated content
    let newEditorState: EditorState;

    if (changeType === 'apply-entity') {
      // For entity updates, use set instead of push to avoid text modifications
      newEditorState = EditorState.set(editorState, {
        currentContent: newContentState
      });
    } else {
      // For other change types, use push as normal
      newEditorState = EditorState.push(editorState, newContentState, changeType);
    }

    onEditorStateChange(newEditorState);

    // Force a re-export by triggering onChange after entity update
    // This ensures resized images get their updated dimensions in the HTML
    setTimeout(() => {
      if (isFunction(onChange)) {
        const htmlOutput = exportHTML(newEditorState);
        onChange({
          raw: convertToRaw(newEditorState.getCurrentContent()),
          html: htmlOutput,
        });
      }
    }, 50);
  }, [editorState, onEditorStateChange, onChange]);

  // Assign the actual function to the ref
  updateEntityDataRef.current = updateEntityData;

  const blockRendererFn = React.useCallback((contentBlock) => {
    const type = contentBlock.getType();

    if (type === 'atomic') {
      return {
        component: AtomicBlock,
        editable: false,
        props: {
          updateEntityData,
          onEditTable: props.onTableEdit,
          onDeleteTable: props.onDeleteTable,
          setEditorDisabled,
        },
      };
    }

    return null;
  }, [updateEntityData, props.onTableEdit, props.onDeleteTable, setEditorDisabled]);

  // Remove the problematic useEffect that was causing infinite loops
  // Initial onChange will be handled by the parent component if needed

  const insertText = (text: string) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const newContentState = Modifier.insertText(contentState, selectionState, text);
    const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');

    onEditorStateChange(newEditorState);
  };

  const onEditorFocused = () => {
    addEvent(EventName.EmailEditorFocused, {
      pathname: location.pathname,
    });
  };

  const focusEditor = () => {
    // @ts-expect-error
    ref?.current?.focus();
  };
  const resetEditor = () => onEditorStateChange(EditorState.createEmpty(decorator));
  useImperativeHandle(ref, () => ({
    focus: () => { },
    reset: resetEditor,
    getValue: (): IEditorState => ({
      raw: convertToRaw(editorState.getCurrentContent()),
      html: exportHTML(editorState),
    }),
    setValue: (value) => {
      const newEditorState = editorStateFromValue({
        value,
        decorator,
        isHtml: props.convertFromHTML,
      });

      setEditorState(newEditorState);
      onEditorStateChange(newEditorState);
    },
    insertText,
    insertEntity: ({ label, data, type }) => {
      const contentState = editorState.getCurrentContent();
      const selectionState = editorState.getSelection();
      const contentStateWithEntity = contentState.createEntity(type, 'IMMUTABLE', { data });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newContentState = Modifier.insertText(contentStateWithEntity, selectionState, label, null, entityKey);
      updateEntityData(newContentState, 'insert-characters');

      setTimeout(() => focusEditor(), 0);
    },
    insertTable: (tableData: TableEntityData) => {
      handleTableSave(tableData);
    },
    updateTable: (entityKey: string, tableData: TableEntityData) => {
      handleTableUpdate(entityKey, tableData);
    },
    deleteTable: (entityKey: string) => {
      handleTableDelete(entityKey);
    },
  }));

  const toggleLink = () => {
    toggleLinkState(editorState)
      .then((linkEditorState) => {
        const selection = linkEditorState.getSelection();
        const blockKey = selection.getStartKey();
        const end = selection.getEndOffset();
        const newEditorState = EditorState.forceSelection(
          linkEditorState,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore TODO: Fix in Node upgrade typing bash!
          new SelectionState({
            anchorKey: blockKey,
            focusKey: blockKey,
            anchorOffset: end,
            focusOffset: end,
          }),
        );

        onEditorStateChange(newEditorState);
      })
      .catch(logger.error);
  };
  const keyBindingFn = (event: React.KeyboardEvent): TCustomKeybindingType => {
    const { keyCode, shiftKey } = event;

    if (!hasCommandModifier(event)) {
      return getDefaultKeyBinding(event);
    }

    // Shift-modified combos
    if (shiftKey) {
      if (keyCode === KeyCode.I) return 'inline-image'; // Cmd/Ctrl+Shift+I
      if (keyCode === KeyCode.V) return 'inline-video'; // Cmd/Ctrl+Shift+V
      if (keyCode === KeyCode.X) return 'strikethrough'; // Cmd/Ctrl+Shift+X
    }

    // Non-shift combos
    if (keyCode === KeyCode.K) return 'link'; // Cmd/Ctrl+K
    if (keyCode === KeyCode.E) return 'code'; // Cmd/Ctrl+E
    if (keyCode === KeyCode.T) return 'table'; // Cmd/Ctrl+T

    // Headings / lists (Cmd/Ctrl+0..8)
    if (KEYCODE_TO_BLOCK_TYPE[keyCode]) {
      return KEYCODE_TO_BLOCK_TYPE[keyCode];
    }

    return getDefaultKeyBinding(event);
  };
  const handleKeyCommand = (command: TCustomKeybindingType): DraftHandleValue => {
    switch (command) {
      case 'bold':
      case 'italic':
      case 'underline':
      case 'strikethrough':
      case 'code': {
        onEditorStateChange(RichUtils.toggleInlineStyle(editorState, toUpper(command)));
        return 'handled';
      }

      case 'unstyled':
      case 'header-one':
      case 'header-two':
      case 'header-three':
      case 'header-four':
      case 'header-five':
      case 'header-six':
      case 'ordered-list-item':
      case 'unordered-list-item': {
        onEditorStateChange(RichUtils.toggleBlockType(editorState, command));
        return 'handled';
      }

      case 'link': {
        toggleLink();
        return 'handled';
      }

      case 'inline-image': {
        // Will be handled by file selection
        return 'handled';
      }

      case 'inline-video': {
        // Will be handled by file selection
        return 'handled';
      }

      case 'cta-button': {
        // Will be handled by CTA button configuration modal
        openCTAButtonModal();
        return 'handled';
      }

      case 'table': {
        openTableModal();
        return 'handled';
      }

      case 'undo': {
        onEditorStateChange(EditorState.undo(editorState));
        return 'handled';
      }

      case 'redo': {
        onEditorStateChange(EditorState.redo(editorState));
        return 'handled';
      }

      default: {
        return 'not-handled';
      }
    }
  };

  const openFileSelector = (inputRef: React.RefObject<HTMLInputElement>) => {
    const fileInput = inputRef.current;

    fileInput?.click();
  };

  const handleImageUpload = React.useCallback(async (file: File) => {
    const validationError = validateImageFile(file);
    if (validationError) {
      logger.error('Image validation failed:', validationError);
      return;
    }

    // Generate a unique upload ID for tracking
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add loading state
    setUploadingImages((prev) => ({
      ...prev,
      [uploadId]: { fileName: file.name, uploadId }
    }));

    try {
      const { url } = await uploadImage(file);

      // Get natural image dimensions for better email compatibility
      const getImageDimensions = (): Promise<{ width: number; height: number }> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
          };
          img.onerror = () => {
            // Fallback to default dimensions if image fails to load
            resolve({ width: 400, height: 300 });
          };
          img.src = url;
        });
      };

      const dimensions = await getImageDimensions();

      const imageData: ImageEntityData = {
        src: url,
        alt: file.name,
        width: dimensions.width,
        height: dimensions.height,
        alignment: 'left',
      };

      // Store initial dimensions in global store as well
      imageResizeStore.setDimensions(url, dimensions.width, dimensions.height);

      // Remove loading state
      setUploadingImages((prev) => {
        const updated = { ...prev };
        delete updated[uploadId];
        return updated;
      });

      // Create and insert the image normally
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', imageData);
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      const newEditorState = AtomicBlockUtils.insertAtomicBlock(
        EditorState.set(editorState, { currentContent: contentStateWithEntity }),
        entityKey,
        ' '
      );

      onEditorStateChange(newEditorState);
    } catch (error) {
      logger.error('Image upload failed:', error);

      // Remove loading state on error
      setUploadingImages((prev) => {
        const updated = { ...prev };
        delete updated[uploadId];
        return updated;
      });

      // TODO: Remove the loading block and show error message
    }
  }, [editorState, uploadImage, validateImageFile, onEditorStateChange]);

  const handleVideoUpload = React.useCallback(async (file: File) => {
    const validationError = validateVideoFile(file);
    if (validationError) {
      logger.error('Video validation failed:', validationError);
      return;
    }

    // Generate a unique upload ID for tracking
    const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Add loading state
    setUploadingVideos((prev) => ({
      ...prev,
      [uploadId]: { fileName: file.name, uploadId }
    }));

    try {
      const { url } = await uploadVideo(file);

      const videoData: VideoEntityData = {
        src: url,
        width: 400,
        height: 300,
        alignment: 'left',
        controls: true,
        autoPlay: false,
        muted: false,
        loop: false,
      };

      // Remove loading state
      setUploadingVideos((prev) => {
        const updated = { ...prev };
        delete updated[uploadId];
        return updated;
      });

      // Create and insert the video normally
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity('VIDEO', 'IMMUTABLE', videoData);
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      const newEditorState = AtomicBlockUtils.insertAtomicBlock(
        EditorState.set(editorState, { currentContent: contentStateWithEntity }),
        entityKey,
        ' '
      );

      onEditorStateChange(newEditorState);
    } catch (error) {
      logger.error('Video upload failed:', error);

      // Remove loading state on error
      setUploadingVideos((prev) => {
        const updated = { ...prev };
        delete updated[uploadId];
        return updated;
      });
    }
  }, [editorState, uploadVideo, validateVideoFile, onEditorStateChange]);

  const handleTextColorChange = React.useCallback((color: TTextColor) => {
    setCurrentTextColor(color);

    // Remove all existing text color styles first
    let newEditorState = editorState;
    const currentStyle = newEditorState.getCurrentInlineStyle();

    currentStyle.forEach((style) => {
      if (style && style.startsWith('TEXT_COLOR_')) {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, style);
      }
    });

    // Add the new color style
    const colorKey = color.replace('#', '').toUpperCase();

    // Add to style map dynamically if not already present
    if (!customStyleMap[`TEXT_COLOR_${colorKey}`]) {
      customStyleMap[`TEXT_COLOR_${colorKey}`] = { color };
    }

    newEditorState = RichUtils.toggleInlineStyle(newEditorState, `TEXT_COLOR_${colorKey}`);
    onEditorStateChange(newEditorState);
  }, [editorState, onEditorStateChange, customStyleMap]);

  const handleHighlightColorChange = React.useCallback((color: THighlightColor) => {
    setCurrentHighlightColor(color);

    // Remove all existing highlight color styles first
    let newEditorState = editorState;
    const currentStyle = newEditorState.getCurrentInlineStyle();

    currentStyle.forEach((style) => {
      if (style && (style.startsWith('HIGHLIGHT_COLOR_') || style === 'HIGHLIGHT_NONE')) {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, style);
      }
    });

    // Add the new highlight style
    if (color === 'transparent') {
      newEditorState = RichUtils.toggleInlineStyle(newEditorState, 'HIGHLIGHT_NONE');
    } else {
      const colorKey = color.replace('#', '').toUpperCase();

      // Add to style map dynamically if not already present
      if (!customStyleMap[`HIGHLIGHT_COLOR_${colorKey}`]) {
        customStyleMap[`HIGHLIGHT_COLOR_${colorKey}`] = { backgroundColor: color };
      }

      newEditorState = RichUtils.toggleInlineStyle(newEditorState, `HIGHLIGHT_COLOR_${colorKey}`);
    }

    onEditorStateChange(newEditorState);
  }, [editorState, onEditorStateChange, customStyleMap]);

  const handleAlignmentChange = React.useCallback((alignment: TTextAlign) => {
    setCurrentAlignment(alignment);

    // For alignment, we'll use inline styles instead of block types
    // Remove all existing alignment styles first
    let newEditorState = editorState;
    const currentStyle = newEditorState.getCurrentInlineStyle();

    currentStyle.forEach((style) => {
      if (style && style.startsWith('ALIGN_')) {
        newEditorState = RichUtils.toggleInlineStyle(newEditorState, style);
      }
    });

    // Add the new alignment style if not 'left' (default)
    if (alignment !== 'left') {
      newEditorState = RichUtils.toggleInlineStyle(newEditorState, `ALIGN_${alignment.toUpperCase()}`);
    }

    onEditorStateChange(newEditorState);
  }, [editorState, onEditorStateChange]);

  const handleIndentChange = React.useCallback((type: TIndentType) => {
    // Create a proper synthetic keyboard event for RichUtils.onTab
    const syntheticEvent = {
      key: 'Tab',
      keyCode: 9,
      which: 9,
      shiftKey: type === 'outdent',
      preventDefault: () => { },
      stopPropagation: () => { }
    } as React.KeyboardEvent<HTMLDivElement>;

    const newEditorState = RichUtils.onTab(syntheticEvent, editorState, 4);

    if (newEditorState && newEditorState !== editorState) {
      onEditorStateChange(newEditorState);
    }
  }, [editorState, onEditorStateChange]);

  const openCTAButtonModal = () => {
    setIsCTAModalOpen(true);
  };

  const handleCTAButtonSave = (config: CTAButtonConfig) => {
    const ctaData: CTAButtonEntityData = {
      text: config.text,
      url: config.url,
      backgroundColor: config.backgroundColor,
      textColor: config.textColor,
      alignment: config.alignment,
    };

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('CTA_BUTTON', 'IMMUTABLE', { data: ctaData });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(editorState, { currentContent: contentStateWithEntity }),
      entityKey,
      ' '
    );

    onEditorStateChange(newEditorState);
    setIsCTAModalOpen(false);
  };

  const handleTableSave = (tableData: TableEntityData) => {
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('TABLE', 'IMMUTABLE', { data: tableData });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(editorState, { currentContent: contentStateWithEntity }),
      entityKey,
      ' '
    );

    onEditorStateChange(newEditorState);
  };

  const handleTableUpdate = (entityKey: string, tableData: TableEntityData) => {
    const contentState = editorState.getCurrentContent();
    const newContentState = contentState.mergeEntityData(entityKey, { data: tableData });
    const newEditorState = EditorState.push(editorState, newContentState, 'change-block-data');

    // Force editor to re-render by moving selection and back
    const selection = newEditorState.getSelection();
    const forceUpdateEditorState = EditorState.forceSelection(newEditorState, selection);
    onEditorStateChange(forceUpdateEditorState);
  };

  const handleTableDelete = (entityKey: string) => {
    const contentState = editorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();

    // Find and remove the block containing this entity
    let blockKeyToRemove = null;
    blockMap.forEach((block, blockKey) => {
      if (block && block.getEntityAt(0) === entityKey) {
        blockKeyToRemove = blockKey;
      }
    });

    if (blockKeyToRemove) {
      // Create a new ContentState with the updated block map
      const newBlockMap = blockMap.delete(blockKeyToRemove);
      const newContentState = ContentState.createFromBlockArray(
        newBlockMap.valueSeq().toArray(),
        contentState.getEntityMap()
      );
      const newEditorState = EditorState.push(editorState, newContentState, 'remove-range');
      onEditorStateChange(newEditorState);
    }
  };

  const openTableModal = () => {
    if (props.onTableModalOpen) {
      props.onTableModalOpen();
    }
  };

  const insertDivider = () => {
    const dividerData: DividerEntityData = {
      style: 'solid',
      color: '#cccccc',
      thickness: 1,
      alignment: 'center',
      width: '100%',
    };

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('DIVIDER', 'IMMUTABLE', dividerData);
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      EditorState.set(editorState, { currentContent: contentStateWithEntity }),
      entityKey,
      ' '
    );

    onEditorStateChange(newEditorState);
  };

  const handleDeleteContent = () => {
    resetEditor();
    props.attachments?.forEach((a) => {
      props.onDeleteAttachment?.(a.id);
    });
    props.onClose?.();
  };

  const handleEmojiSelect = React.useCallback((emoji: string) => {
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    // If there's a selection, collapse it to the end point to insert at cursor
    const targetSelection = selectionState.isCollapsed()
      ? selectionState
      : selectionState.merge({
        anchorOffset: selectionState.getEndOffset(),
        focusOffset: selectionState.getEndOffset(),
      });

    const newContentState = Modifier.insertText(contentState, targetSelection, emoji);
    const newEditorState = EditorState.push(editorState, newContentState, 'insert-characters');

    // Move cursor to the end of inserted emoji
    const newSelection = newEditorState.getSelection().merge({
      anchorOffset: targetSelection.getStartOffset() + emoji.length,
      focusOffset: targetSelection.getStartOffset() + emoji.length,
    });

    const finalEditorState = EditorState.forceSelection(newEditorState, newSelection);
    onEditorStateChange(finalEditorState);
  }, [editorState, onEditorStateChange]);

  const editorHasContent = React.useMemo(
    () => editorState.getCurrentContent().hasText() || Boolean(props.attachments?.length),
    [editorState, props.attachments],
  );

  const actionsRef = React.useRef();

  /**
   * TODO<developer>: this is a hacky way to disable action button
   * Action buttons should be build as a separate component, and accept props for 'disabled' state
   * Right now actions live 3 components above in hierarchy and there is no way to control it's state
   */
  useEffect(() => {
    if (!editorHasContent || props.errorHeader) {
      // @ts-expect-error fix ref current
      actionsRef?.current?.querySelector('button')?.setAttribute('disabled', true);
    } else {
      // @ts-expect-error fix ref current
      actionsRef?.current?.querySelector('button')?.removeAttribute('disabled');
    }
  }, [editorHasContent, props.errorHeader]);

  return (
    <div
      className={cx(styles.Editor, props.className, {
        [styles.disabled]: props.disabled,
      })}
      onClick={focusEditor}
    >
      <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }} className={styles.editor}>
        <div className={styles.editorContainer}>
          {!!props.errorHeader && <>{props.errorHeader}</>}

          {/* Show loading indicators for uploading images */}
          {Object.keys(uploadingImages).length > 0 && (
            <div className="mb-3" role="status" aria-live="polite" aria-label="Images uploading">
              {Object.values(uploadingImages).map((upload) => (
                <ImageLoadingPlaceholder
                  key={upload.uploadId}
                  fileName={upload.fileName}
                  width={300}
                  height={200}
                />
              ))}
            </div>
          )}

          {/* Show loading indicators for uploading videos */}
          {Object.keys(uploadingVideos).length > 0 && (
            <div className="mb-3" role="status" aria-live="polite" aria-label="Videos uploading">
              {Object.values(uploadingVideos).map((upload) => (
                <ImageLoadingPlaceholder
                  key={upload.uploadId}
                  fileName={upload.fileName}
                  width={400}
                  height={300}
                />
              ))}
            </div>
          )}

          {/* @ts-ignore - DraftEditor has incompatible refs property with React 18 */}
          <DraftEditor
            readOnly={editorDisabled || props.readOnly || isCTAModalOpen}
            editorState={editorState}
            placeholder={!blockStyle && (props.placeholder || 'Enter your message...')}
            onChange={onEditorStateChange}
            onFocus={onEditorFocused}
            keyBindingFn={keyBindingFn}
            handleKeyCommand={handleKeyCommand}
            blockRendererFn={blockRendererFn}
            customStyleMap={customStyleMap}
            // @ts-expect-error
            ref={ref}
            spellCheck={false}
          />
        </div>

        {!!props.signaturePreview && (
          <div
            style={{ wordBreak: 'break-word', whiteSpace: 'pre-line', paddingTop: '1rem' }}
            className={styles.signature}
            dangerouslySetInnerHTML={{
              __html: props.signaturePreview,
            }}
          />
        )}

      </div>
      {!isEmpty(props.attachments) && (
        <div className={styles.attachments}>
          {map(props.attachments, (attachment) => (
            <AttachmentItem
              key={attachment.id}
              className={styles.item}
              content={attachment}
              resourceType={props.resourceType}
              onDelete={props.onDeleteAttachment?.bind(this, attachment.id)}
            />
          ))}
        </div>
      )}
      {!props.readOnly && !props.plaintextOnly && (
        <div className={`${styles.editorSpace} flex gap-2`}>
          <StyleControls
            editorState={editorState}
            getSelectedEntityType={() => getSelectedEntityType(editorState)}
            toggleBlockType={(type) => {
              onEditorStateChange(RichUtils.toggleBlockType(editorState, type));
              setBlockStyle((prevState) => (prevState === type ? undefined : type));
            }}
            toggleInlineStyle={(style) => {
              onEditorStateChange(RichUtils.toggleInlineStyle(editorState, style));
            }}
            toggleMediaType={(type, ref) => {
              if (type === 'link') {
                toggleLink();
              } else if (type === 'attachment') {
                openFileSelector(ref);
              } else if (type === 'inline-image') {
                // Create a temporary file input for image selection
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = (e) => {
                  const files = (e.target as HTMLInputElement).files;
                  if (files && files[0]) {
                    handleImageUpload(files[0]);
                  }
                };
                input.click();
              } else if (type === 'inline-video') {
                // Create a temporary file input for video selection
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'video/*';
                input.onchange = (e) => {
                  const files = (e.target as HTMLInputElement).files;
                  if (files && files[0]) {
                    handleVideoUpload(files[0]);
                  }
                };
                input.click();
              } else if (type === 'cta-button') {
                openCTAButtonModal();
              } else if (type === 'table') {
                openTableModal();
              } else if (type === 'divider') {
                insertDivider();
              } else if (type === 'quote') {
                // Use toggleBlockType to apply blockquote
                onEditorStateChange(RichUtils.toggleBlockType(editorState, 'blockquote'));
              }
            }}
            onFilesSelected={props.onFilesSelected}
            ref={ref}
            showProgramVariables={props.showProgramVariables}
            enableVariableReplacement={props.enableVariableReplacement}
            enableEmailTemplate={props.enableEmailTemplate}
            invalidMemberIdsForField={props.invalidMemberIdsForField}
            additionalVariables={props.additionalVariables}
            onTemplateSelected={props.onTemplateSelected}
            enableMessageTemplate={props.enableMessageTemplate}
            currentTextColor={currentTextColor}
            currentHighlightColor={currentHighlightColor}
            currentAlignment={currentAlignment}
            onTextColorChange={handleTextColorChange}
            onHighlightColorChange={handleHighlightColorChange}
            onAlignmentChange={handleAlignmentChange}
            onIndentChange={handleIndentChange}
            onEmojiSelect={handleEmojiSelect}
            emailV2={emailV2}
          />
          <div className="flex gap-2">
            <Button className={styles.trashButton} onClick={handleDeleteContent}>
              <TrashIcon />
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div ref={actionsRef} style={{ display: !props.canSend ? 'block' : 'none' }}>
                    {React.Children.toArray(props.actions)}
                  </div>
                </TooltipTrigger>
                {!props.canSend && (
                  <TooltipContent>
                    <p>{props.errorMessage}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
            {props.canSend && (
              <div ref={actionsRef}>
                {React.Children.toArray(props.actions)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA Button Configuration Modal */}
      <CTAButtonConfigModal
        isOpen={isCTAModalOpen}
        onClose={() => setIsCTAModalOpen(false)}
        onSave={handleCTAButtonSave}
      />

    </div>
  );
};

export const Editor = React.forwardRef(EditorComponent);
